import type { AppDispatch } from '../store';
import { aidesApiSlice } from '../features/aides/aidesApiSlice';
import { actionsApiSlice } from '../features/actions/actionApiSlice';

/** Action code of the shared "Call LLM Port key" action; its source_id is resolved at call time. */
const CALL_LLM_ACTION_CODE = 'CALLLLMPK';

/** Model shared by every frontend LLM caller so responses stay consistent across features. */
export const CALL_LLM_DEFAULT_MODEL = 'gemini-2.0-flash-thinking-exp-1219';

export interface CallLLMActionOptions {
  dispatch: AppDispatch;
  systemPrompt: string;
  userPrompt: string;
  model?: string;
  maxCompletionTokens?: number;
  actionCode?: string;
}

const actionSourceIdCache = new Map<string, string>();

async function resolveActionSourceId(dispatch: AppDispatch, actionCode: string): Promise<string> {
  const cached = actionSourceIdCache.get(actionCode);
  if (cached) return cached;
  const { data, error } = await dispatch(
    aidesApiSlice.endpoints.getAiWorkersBySourceType.initiate('action')
  );
  if (error) {
    throw new Error((error as any)?.message || 'Failed to resolve LLM action configuration.');
  }
  const match = Array.isArray(data) ? data.find((item: any) => item?.code === actionCode) : undefined;
  const sourceId = match?.source_id;
  if (!sourceId) {
    throw new Error(`No integration source configured for action code "${actionCode}".`);
  }
  actionSourceIdCache.set(actionCode, sourceId);
  return sourceId;
}

/**
 * Warms the source_id cache for the given action code ahead of time (e.g. on screen mount), so the
 * `GET api/v1/sources?source_type=action` lookup isn't sitting in front of the first voice-triggered
 * `callLLMAction` call. Best-effort: failures are swallowed since `callLLMAction` will re-resolve
 * (and surface any real error) when it's actually invoked.
 */
export function prefetchCallLLMActionSource(
  dispatch: AppDispatch,
  actionCode: string = CALL_LLM_ACTION_CODE
): void {
  resolveActionSourceId(dispatch, actionCode).catch(() => undefined);
}

export function resolveCallLLMActionSourceId(
  dispatch: AppDispatch,
  actionCode: string = CALL_LLM_ACTION_CODE
): Promise<string> {
  return resolveActionSourceId(dispatch, actionCode);
}

/**
 * Calls the shared "Call LLM Port key" action (`POST api/v3/integrations`) instead of hitting
 * Portkey directly from the browser. The action's source_id is resolved from its action code,
 * and the assistant message content is returned.
 */
export async function callLLMAction({
  dispatch,
  systemPrompt,
  userPrompt,
  model = CALL_LLM_DEFAULT_MODEL,
  maxCompletionTokens = 250,
  actionCode = CALL_LLM_ACTION_CODE,
}: CallLLMActionOptions): Promise<string> {
  const sourceId = await resolveActionSourceId(dispatch, actionCode);
  const { data, error } = await dispatch(
    actionsApiSlice.endpoints.uploadInternalAction.initiate({
      source_id: sourceId,
      payload: {
        model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        max_completion_tokens: maxCompletionTokens,
      },
    })
  );
  if (error) {
    throw new Error((error as any)?.message || 'LLM action call failed.');
  }
  // The integration returns `data` as an array with one completion object: [{ choices: [...] }].
  const completion = Array.isArray(data) ? data[0] : data;
  const content = completion?.choices?.[0]?.message?.content;
  if (typeof content !== 'string') {
    throw new Error('Invalid response format from LLM action.');
  }
  return content;
}
